/**
 * Enhanced Location Selector Component
 *
 * Cascading selectors for Country → State → City using:
 * - country-state-city: Countries only (frontend)
 * - Backend API: States and Cities (dr5hn database with filtering)
 *
 * Features:
 * - Priority countries (Spain, Mexico, Argentina, etc.) at the top
 * - Country names in Spanish
 * - For Spain: Only provinces (not autonomous communities)
 * - Excludes districts/neighborhoods (type="section")
 * - Native HTML selects styled with Tailwind CSS
 */

import { useState, useEffect } from 'preact/hooks';
import { Country, ICountry } from 'country-state-city';
import { getCountryNameInSpanish } from '../../utils/locationTranslations';
import { getStates, getCities, type State, type City } from '../../services/locationService';

interface LocationSelectorProps {
  // Current values (names as strings)
  countryValue?: string;
  stateValue?: string;
  cityValue?: string;

  // Change callbacks (receive name strings)
  onCountryChange: (countryName: string) => void;
  onStateChange: (stateName: string) => void;
  onCityChange: (cityName: string) => void;

  // Validation errors
  errors?: {
    country?: string;
    state?: string;
    city?: string;
  };

  // Required fields
  required?: {
    country?: boolean;
    state?: boolean;
    city?: boolean;
  };

  // Layout mode: 'row' = 2 columns on desktop, 'column' = always 1 column
  layout?: 'row' | 'column';
}

// Internal state interface matching backend State type
interface StateOption {
  name: string;
  iso2: string;
  type?: string;
}

// Priority countries (shown first in the list)
const PRIORITY_COUNTRIES = [
  'ES', // España (main market - fitbook.es)
  'MX', // México
  'AR', // Argentina
  'CO', // Colombia
  'CL', // Chile
  'PE', // Perú
  'VE', // Venezuela
  'UY', // Uruguay
];

// Country flag emoji helper
const getCountryFlag = (isoCode: string): string => {
  return isoCode
    .toUpperCase()
    .split('')
    .map((char) => String.fromCodePoint(127397 + char.charCodeAt(0)))
    .join('');
};

export function LocationSelector({
  countryValue,
  stateValue,
  cityValue,
  onCountryChange,
  onStateChange,
  onCityChange,
  errors = {},
  required = { country: true, state: true, city: true },
  layout = 'row'
}: LocationSelectorProps) {
  // Internal state
  const [selectedCountry, setSelectedCountry] = useState<ICountry | null>(null);
  const [selectedState, setSelectedState] = useState<StateOption | null>(null);
  const [availableStates, setAvailableStates] = useState<StateOption[]>([]);
  const [availableCities, setAvailableCities] = useState<City[]>([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);

  // Get all countries sorted with priority ones first
  const allCountries = Country.getAllCountries();
  const priorityCountriesList = allCountries.filter((c) =>
    PRIORITY_COUNTRIES.includes(c.isoCode)
  );
  const otherCountries = allCountries.filter(
    (c) => !PRIORITY_COUNTRIES.includes(c.isoCode)
  );

  // Sort priority countries by the order defined in PRIORITY_COUNTRIES
  const sortedPriorityCountries = priorityCountriesList.sort((a, b) => {
    return PRIORITY_COUNTRIES.indexOf(a.isoCode) - PRIORITY_COUNTRIES.indexOf(b.isoCode);
  });

  // Sort other countries alphabetically by Spanish name
  const sortedOtherCountries = otherCountries.sort((a, b) => {
    const nameA = getCountryNameInSpanish(a.isoCode, a.name);
    const nameB = getCountryNameInSpanish(b.isoCode, b.name);
    return nameA.localeCompare(nameB, 'es');
  });

  // Initialize from provided values
  useEffect(() => {
    const initializeFromValues = async () => {
      if (countryValue) {
        const country = allCountries.find(
          (c) => c.name.toLowerCase() === countryValue.toLowerCase()
        );
        if (country) {
          setSelectedCountry(country);
          setLoadingStates(true);
          try {
            const states = await getStates(country.isoCode);
            setAvailableStates(states);

            if (stateValue) {
              const state = states.find(
                (s) => s.name.toLowerCase() === stateValue.toLowerCase()
              );
              if (state) {
                setSelectedState(state);
                setLoadingCities(true);
                try {
                  const cities = await getCities(country.isoCode, state.iso2);
                  setAvailableCities(cities);
                } catch (error) {
                  console.error('Error loading cities:', error);
                  setAvailableCities([]);
                } finally {
                  setLoadingCities(false);
                }
              }
            }
          } catch (error) {
            console.error('Error loading states:', error);
            setAvailableStates([]);
          } finally {
            setLoadingStates(false);
          }
        }
      }
    };

    initializeFromValues();
  }, []); // Only run on mount

  // Handle country change
  const handleCountryChange = async (e: Event) => {
    const select = e.currentTarget as HTMLSelectElement;
    const isoCode = select.value;

    if (!isoCode) {
      // Clear selection
      setSelectedCountry(null);
      setSelectedState(null);
      setAvailableStates([]);
      setAvailableCities([]);
      onCountryChange('');
      onStateChange('');
      onCityChange('');
      return;
    }

    const country = allCountries.find((c) => c.isoCode === isoCode);
    if (country) {
      setSelectedCountry(country);
      setSelectedState(null);
      setAvailableCities([]);
      onCountryChange(country.name);
      onStateChange('');
      onCityChange('');

      // Load states from API
      setLoadingStates(true);
      setAvailableStates([]);
      try {
        const states = await getStates(country.isoCode);
        setAvailableStates(states);
      } catch (error) {
        console.error('Error loading states:', error);
        setAvailableStates([]);
      } finally {
        setLoadingStates(false);
      }
    }
  };

  // Handle state change
  const handleStateChange = async (e: Event) => {
    const select = e.currentTarget as HTMLSelectElement;
    const stateCode = select.value;

    if (!stateCode || !selectedCountry) {
      // Clear selection
      setSelectedState(null);
      setAvailableCities([]);
      setLoadingCities(false);
      onStateChange('');
      onCityChange('');
      return;
    }

    const state = availableStates.find((s) => s.iso2 === stateCode);
    if (state) {
      setSelectedState(state);
      setLoadingCities(true);
      setAvailableCities([]);
      onStateChange(state.name);
      onCityChange('');

      try {
        const cities = await getCities(selectedCountry.isoCode, state.iso2);
        setAvailableCities(cities);
      } catch (error) {
        console.error('Error loading cities:', error);
        setAvailableCities([]);
      } finally {
        setLoadingCities(false);
      }
    }
  };

  // Handle city change
  const handleCityChange = (e: Event) => {
    const select = e.currentTarget as HTMLSelectElement;
    const cityName = select.value;

    if (!cityName) {
      onCityChange('');
      return;
    }

    onCityChange(cityName);
  };

  return (
    <div class={`grid gap-6 ${layout === 'column' ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2'}`}>
      {/* Country Selector */}
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">
          País {required.country && <span class="text-red-500">*</span>}
        </label>
        <select
          value={selectedCountry?.isoCode || ''}
          onChange={handleCountryChange}
          class={`block w-full px-3 py-2 border rounded-md shadow-sm text-sm focus:outline-none focus:ring-2 transition ${
            errors.country
              ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
              : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
          }`}
          aria-label="Selector de país"
          title="Selecciona un país"
        >
          <option value="">Selecciona país</option>

          {/* Priority Countries */}
          {sortedPriorityCountries.map((country) => (
            <option key={country.isoCode} value={country.isoCode}>
              {getCountryFlag(country.isoCode)} {getCountryNameInSpanish(country.isoCode, country.name)}
            </option>
          ))}

          {/* Separator */}
          {sortedPriorityCountries.length > 0 && sortedOtherCountries.length > 0 && (
            <option disabled>───────────</option>
          )}

          {/* Other Countries */}
          {sortedOtherCountries.map((country) => (
            <option key={country.isoCode} value={country.isoCode}>
              {getCountryFlag(country.isoCode)} {getCountryNameInSpanish(country.isoCode, country.name)}
            </option>
          ))}
        </select>
        {errors.country && (
          <p class="mt-1 text-sm text-red-600">{errors.country}</p>
        )}
      </div>

      {/* State/Province Selector */}
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">
          Provincia/Estado {required.state && <span class="text-red-500">*</span>}
        </label>
        <select
          value={selectedState?.iso2 || ''}
          onChange={handleStateChange}
          disabled={!selectedCountry || loadingStates || availableStates.length === 0}
          class={`block w-full px-3 py-2 border rounded-md shadow-sm text-sm focus:outline-none focus:ring-2 transition ${
            errors.state
              ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
              : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
          } ${!selectedCountry || loadingStates || availableStates.length === 0 ? 'bg-gray-100 cursor-not-allowed' : ''}`}
          aria-label="Selector de provincia o estado"
          title="Selecciona una provincia o estado"
        >
          <option value="">
            {!selectedCountry
              ? 'Selecciona primero un país'
              : loadingStates
              ? 'Cargando provincias...'
              : availableStates.length === 0
              ? 'No hay provincias disponibles'
              : 'Selecciona provincia/estado'}
          </option>
          {availableStates.map((state) => (
            <option key={state.iso2} value={state.iso2}>
              {state.name}
            </option>
          ))}
        </select>
        {errors.state && (
          <p class="mt-1 text-sm text-red-600">{errors.state}</p>
        )}
      </div>

      {/* City Selector */}
      <div class={layout === 'column' ? '' : 'sm:col-span-2'}>
        <label class="block text-sm font-medium text-gray-700 mb-1">
          Ciudad {required.city && <span class="text-red-500">*</span>}
        </label>
        <select
          value={cityValue || ''}
          onChange={handleCityChange}
          disabled={!selectedState || loadingCities || availableCities.length === 0}
          class={`block w-full px-3 py-2 border rounded-md shadow-sm text-sm focus:outline-none focus:ring-2 transition ${
            errors.city
              ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
              : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
          } ${!selectedState || loadingCities || availableCities.length === 0 ? 'bg-gray-100 cursor-not-allowed' : ''}`}
          aria-label="Selector de ciudad"
          title="Selecciona una ciudad"
        >
          <option value="">
            {!selectedState
              ? 'Selecciona primero provincia/estado'
              : loadingCities
              ? 'Cargando ciudades...'
              : availableCities.length === 0
              ? 'No hay ciudades disponibles'
              : 'Selecciona ciudad'}
          </option>
          {availableCities.map((city, index) => (
            <option key={`${city.name}-${index}`} value={city.name}>
              {city.name}
            </option>
          ))}
        </select>
        {errors.city && (
          <p class="mt-1 text-sm text-red-600">{errors.city}</p>
        )}
      </div>
    </div>
  );
}
